import React, { ComponentProps, useCallback, useMemo } from 'react'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import TextButton from 'LuxKit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind' | 'children'> {
  rooms: Array<App.RoomOccupants>
  checkIn?: string
  roomType?: App.PackageRoomType
  packageOptions: Array<App.OfferPackageOptionView>
  isFlightSelected?: boolean
  activeFilter?: OfferPageFilterType
  onSubmit?: () => void
  durationSelected?: boolean
}

function AccommodationMobileViewOptionsButton(props: Props) {
  const {
    rooms,
    checkIn,
    roomType,
    packageOptions,
    isFlightSelected,
    activeFilter,
    onSubmit,
    onClick,
    durationSelected,
    ...rest
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const invalidRoomSelection = !hasSelectedRooms(rooms) || isRoomsInvalid(rooms)
  const viewOptionsLabel = useMemo(() => {
    if (!checkIn) return 'Check availability'
    return 'Select room'
  }, [checkIn])

  const onViewOptionsClick = useCallback(async(e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e)

    if (invalidRoomSelection || !durationSelected) {
      e.preventDefault()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        room: roomType,
        flights: !!isFlightSelected,
      })
      return
    }

    if (!checkIn) {
      e.preventDefault()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        room: roomType,
        flights: !!isFlightSelected,
      })
      return
    }

    // Reset bedbank room id for Single Property pages
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: undefined })
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'packageSelection' })
  }, [onClick, invalidRoomSelection, durationSelected, checkIn, offerPageDispatch, roomType, isFlightSelected])

  return (
    <TextButton {...rest} kind="primary" onClick={onViewOptionsClick}>
      {viewOptionsLabel}
    </TextButton>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  packageOptions: getDurationPackageOptions(state, appState),
  isFlightSelected: state.flights,
  activeFilter: state.activeFilter,
  durationSelected: state.durationSelected,
}), AccommodationMobileViewOptionsButton)
